'use client';

import { c as _c } from "react/compiler-runtime";
import { zodResolver } from '@hookform/resolvers/zod';
import { Plus } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { ChangeEvent, MouseEvent, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import EditMedia from '@/app/[locale]/(worlds)/profile/components/EditMedia';
import MediaGallery from '@/app/[locale]/(worlds)/profile/components/MediaGallery';
import NoMedia from '@/app/[locale]/(worlds)/profile/components/NoMedia';
import ViverseButton from '@/components/ViverseButton';
import IconEdit from '@/components/assets/icon_edit.svg';
import { Form, FormControl, FormField, FormItem } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ACCEPTED_FILE_TYPES, MAX_IMAGE_SIZE, MAX_VIDEO_SIZE } from '@/constants/file';
import { useConfigsContext } from '@/context/ConfigsContext';
import { useFetchBlendVisionVodInfo } from '@/hooks/useFetchBlendVisionVodInfo';
import { usePollBlendVisionVodStatus } from '@/hooks/usePollBlendVisionVodStatus';
import { useUploadErrorMessages } from '@/hooks/useUploadErrorMessages';
import { useUploadMediaToService } from '@/hooks/useUploadMediaToService';
import { createDefaultAssets, generateFilePreview } from '@/lib/asset';
import { validateImageDimensions } from '@/lib/image';
import { showToast } from '@/lib/toast';
import { cn } from '@/lib/utils';
import { updateProfileMeByCmsAccountId } from '@/server/lib/profile';
import { deleteFile, deleteVod } from '@/server/services/blendvision';
import { CreateVodResponse } from '@/server/services/types';
import { Asset } from '@/types/asset';
const MAX_FILES = 25;
function arraySwap<T>(array: T[], index1: number, index2: number): T[] {
  const newArray = [...array];
  [newArray[index1], newArray[index2]] = [newArray[index2], newArray[index1]];
  return newArray;
}
const useFormSchema = () => {
  const $ = _c(2);
  const errorMessages = useUploadErrorMessages();
  let t0;
  if ($[0] !== errorMessages) {
    t0 = z.object({
      media: z.array(z.instanceof(File)).refine(_temp2, {
        message: errorMessages["profile-media-file-too-large"].description
      }).nullable()
    });
    $[0] = errorMessages;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const formSchema = t0;
  return formSchema;
};
function UserPhotoVideos() {
  const t = useTranslations();
  const {
    store,
    setStore
  } = useConfigsContext();
  const htcToken = store?.credentials?.htcToken;
  const cmsAccountId = store?.profile?.id;
  const defaultAssets = createDefaultAssets(store?.profile?.medias, true);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [assets, setAssets] = useState<Asset[]>(defaultAssets);
  const [cachedAssets, setCachedAssets] = useState<Asset[]>(defaultAssets);
  const fileInput = useRef<HTMLInputElement>(null);
  const errorMessages = useUploadErrorMessages();
  const formSchema = useFormSchema();
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      media: []
    }
  });
  const {
    reset,
    formState: {
      errors
    }
  } = form;
  const uploadMediaToService = useUploadMediaToService();
  useFetchBlendVisionVodInfo(assets, setAssets, 'private');
  usePollBlendVisionVodStatus(assets, setAssets, 'private');
  useEffect(() => {
    assets.forEach(asset => {
      // 🥷🏼 use this way to cache the assets that have a playback token
      if (asset.video && asset.video.vodId && asset.video.isConverted) {
        setCachedAssets(prev => prev.map(a => a.video?.vodId === asset.video?.vodId ? {
          ...a,
          video: asset.video
        } : a));
      }
    });
  }, [assets]);
  const handleDelete = (index: number) => {
    setAssets(prev_0 => prev_0.filter((_, i) => i !== index));
    const media = form.watch('media')!;
    form.setValue('media', media?.filter((__0, i_0) => i_0 !== index));

    // 🥷🏼 this is a hack to clear the file input
    if (fileInput.current) {
      fileInput.current.value = '';
    }
  };
  const handleReorder = (fromIndex: number, toIndex: number) => {
    setAssets(prev_1 => {
      const newAssets = arraySwap(prev_1, fromIndex, toIndex);
      const newMedia = newAssets.map(asset_0 => asset_0.upload?.file).filter((file): file is File => file !== null && file !== undefined);
      form.setValue('media', newMedia.length > 0 ? newMedia : null);
      return newAssets;
    });
  };
  const handleCancel = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    reset();
    const newAssets_0 = defaultAssets.map(asset_1 => ({
      ...asset_1,
      ...(asset_1.video && {
        video: cachedAssets.find(a_0 => a_0.video?.vodId === asset_1.video?.vodId)?.video || asset_1.video
      })
    }));
    setAssets(newAssets_0);
    setIsEditing(false);
  };
  const handleFileChange = async (event: ChangeEvent<HTMLInputElement>, onChange: (files: File[]) => void) => {
    if (!event.target.files) return;
    const media_0 = form.watch('media');
    const files = Array.from(event.target.files);
    if (files.length + (media_0?.length ?? 0) > MAX_FILES) {
      showToast({
        message: t.rich('upload-errors.max-files.description', {
          maxFiles: MAX_FILES
        }),
        type: 'error'
      });
      return;
    }
    form.clearErrors('media');
    const validFiles: File[] = [];
    for (const file_0 of files) {
      if (!ACCEPTED_FILE_TYPES.includes(file_0.type as (typeof ACCEPTED_FILE_TYPES)[number])) {
        showToast({
          message: errorMessages['unsupported-file-type-1'].description,
          type: 'error'
        });
        continue;
      }
      if (file_0.type.startsWith('image/')) {
        if (file_0.size > MAX_IMAGE_SIZE) {
          showToast({
            message: errorMessages['profile-media-file-too-large'].description,
            type: 'error'
          });
          continue;
        }
        const dimensionValidation = await validateImageDimensions(file_0);
        if (dimensionValidation === 'unreadable') {
          showToast({
            message: errorMessages['unsupported-file-type-1'].description,
            type: 'error'
          });
          continue;
        }
        if (dimensionValidation === 'too-large') {
          showToast({
            message: errorMessages['image-dimensions-too-large'].description,
            type: 'error'
          });
          continue;
        }
      } else {
        if (file_0.size > MAX_VIDEO_SIZE) {
          showToast({
            message: errorMessages['profile-media-file-too-large'].description,
            type: 'error'
          });
          continue;
        }
      }
      if (assets.some(asset_2 => asset_2.upload?.file?.name === file_0.name)) {
        showToast({
          message: t('upload-errors.same-file-already-uploaded.description'),
          type: 'error'
        });
        continue;
      }
      validFiles.push(file_0);
    }
    setAssets(prev_2 => [...prev_2, ...validFiles.map(generateFilePreview)]);
    onChange(media_0 ? [...media_0, ...validFiles] : validFiles);
  };
  const uploadDone = (index_0: number) => {
    setAssets(prev_3 => prev_3.map((a_1, i_1) => {
      return i_1 === index_0 ? {
        ...a_1,
        upload: {
          ...a_1.upload!,
          progress: 100,
          uploading: false
        }
      } : a_1;
    }));
  };
  const uploadStart = (index_1: number) => {
    setAssets(prev_4 => prev_4.map((a_2, i_2) => {
      return i_2 === index_1 ? {
        ...a_2,
        upload: {
          ...a_2.upload!,
          uploading: true,
          progress: 0
        }
      } : a_2;
    }));
  };
  const updateProgress = (progress: number, index_2: number) => {
    setAssets(prev_5 => prev_5.map((a_3, i_3) => {
      return i_3 === index_2 ? {
        ...a_3,
        upload: {
          ...a_3.upload!,
          progress
        }
      } : a_3;
    }));
  };
  const updateAsset = (result: string | CreateVodResponse, index_3: number) => {
    setAssets(prev_6 => prev_6.map((a_4, i_4) => {
      // 🥷🏼 this is a hack to make the upload progress animation linearly
      setTimeout(() => {
        setAssets(prev_7 => prev_7.map((a_5, i_5) => {
          return i_5 === index_3 ? {
            ...a_5,
            upload: {
              ...a_5.upload!,
              uploading: false
            }
          } : a_5;
        }));
      }, 500);
      if (i_4 !== index_3) return a_4;
      const common = {
        ...a_4,
        upload: {
          ...a_4.upload!,
          progress: 100
        }
      };
      return typeof result === 'string' ? {
        ...common,
        image: {
          url: result
        }
      } : {
        ...common,
        video: {
          ...a_4.video!,
          vodId: result.vod?.id
        }
      };
    }));
  };
  const onSubmit = async () => {
    if (!htcToken || !cmsAccountId) {
      throw new Error('No HTC token or CMS account ID');
    }
    setIsSaving(true);
    try {
      const promises = assets.map((asset_3, index_4) => {
        if (
        // 🧬 has already uploaded to world-assets
        asset_3.image && asset_3.image.url && asset_3.image.url.toLowerCase().startsWith('https://world-assets')) {
          uploadDone(index_4);
          return Promise.resolve({
            image: asset_3.image
          });
        }

        // 🧬 has already uploaded to blendvision
        if (asset_3.video && (asset_3.video.isConverted || asset_3.video.vodId)) {
          uploadDone(index_4);
          return Promise.resolve({
            video: asset_3.video
          });
        }
        uploadStart(index_4);
        return uploadMediaToService({
          file: asset_3.upload!.file!,
          index: index_4,
          updateProgress
        }).then(async result_0 => {
          if (!result_0) return;
          updateAsset(result_0, index_4);
          return result_0;
        });
      });
      const results = await Promise.all(promises);
      const medias = results.filter(result_1 => result_1).map(result_2 => {
        // 🧬 response with image url
        if (typeof result_2 === 'string') {
          return {
            image: {
              url: result_2
            }
          };
        }

        // ✅ already uploaded
        if ('video' in result_2!) {
          return {
            video: {
              vod_id: result_2.video.vodId,
              cover_image: result_2.video.coverImage,
              file_id: result_2.video.fileId
            }
          };
        }

        // ✅ already uploaded
        if ('image' in result_2!) {
          return {
            image: result_2.image
          };
        }

        // 🧬 response with vod id
        return {
          video: {
            vod_id: result_2!.vod.id,
            cover_image: result_2!.vod.cover_image?.auto.url,
            file_id: result_2!.vod.source.library.video.id
          }
        };
      });
      const deletedMedias = defaultAssets.filter(asset_4 => asset_4.video && asset_4.video.vodId && asset_4.video.fileId && !assets.some(a_6 => a_6.video?.vodId === asset_4.video?.vodId));
      const promisesDeletedMedias = deletedMedias.map(media_1 => Promise.all([deleteVod(media_1.video!.vodId), deleteFile(media_1.video!.fileId)]));
      const [res] = await Promise.all([updateProfileMeByCmsAccountId(cmsAccountId, {
        profile_media: medias
      }), ...promisesDeletedMedias]);
      if (res?.status === 200 && res?.results) {
        setStore(value => ({
          ...value,
          profile: {
            ...value.profile!,
            medias
          }
        }));
      } else {
        throw new Error('Failed to update profile avatar');
      }
    } catch (error) {
      console.error('Failed to update profile media: ', error);
      showToast({
        message: errorMessages['connection-issue'].description,
        type: 'error'
      });
    } finally {
      setTimeout(() => {
        reset();
        setIsEditing(false);
        setIsSaving(false);
      }, 500);
    }
  };
  return <div id="medias" className="flex flex-col gap-6 px-4 md:gap-9 md:px-8 lg:px-18">
      <div className="flex items-center">
        <div className="flex w-full flex-col gap-[10px]">
          <div className="flex items-center justify-between">
            <h2 className="header-5 md:header-4">{t('profile-page.anchors.photos-videos')}</h2>
            {assets.length > 0 && !isEditing && <ViverseButton variant="icon3" className="group h-[34px] px-4 py-[7px] text-[14px] md:h-[48px] md:px-5 md:py-3 md:text-[18px]" onClick={() => setIsEditing(true)}>
                <IconEdit className="-mt-1 mr-2 size-5" />
                {t('profile-page.edit-media')}
              </ViverseButton>}
          </div>
          <p className="body-2 md:text-[18px]">
            {t('profile-page.anchors.photos-videos-description-2')}
          </p>
        </div>
      </div>

      <div className={cn('flex w-full flex-col items-center justify-center gap-5 rounded-2xl', {
      'bg-none': assets.length > 0 && !isEditing,
      'bg-secondary-normal': assets.length === 0 || isEditing
    })}>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="relative w-full">
            {isEditing ? <EditMedia isSaving={isSaving} assets={assets} error={!!errors.media} handleDelete={handleDelete} handleCancel={handleCancel} handleReorder={handleReorder} uploadSlot={<FormField control={form.control} name="media" render={({
            field: {
              onChange: onChange_0
            }
          }) => <FormItem>
                        <FormControl>
                          <Label className="hover:bg-vive-surface-normal/40 group relative grid aspect-[326/191] w-full cursor-pointer place-items-center rounded-2xl transition-all md:aspect-[306/180]" htmlFor="media">
                            <svg className="absolute inset-0 stroke-white/80 transition-all duration-500 group-hover:stroke-white" viewBox="0 0 306 180" xmlns="http://www.w3.org/2000/svg">
                              <rect x="1" y="1" width="304" height="178" fill="none" strokeWidth="1" strokeDasharray="7 7" rx="6" />
                            </svg>
                            <div className="flex flex-col items-center gap-2">
                              <Plus className="size-6" />
                              <p className="subheader-2">{t('profile-page.add-more')}</p>
                              <Input id="media" key={fileInput?.current?.name} type="file" className="hidden" accept={ACCEPTED_FILE_TYPES.join(',')} ref={fileInput} multiple onChange={event_0 => handleFileChange(event_0, onChange_0)} />
                            </div>
                          </Label>
                        </FormControl>
                      </FormItem>} />} /> : assets.length > 0 ? <MediaGallery assets={assets} /> : <NoMedia onStartEditing={() => setIsEditing(true)} />}
          </form>
        </Form>
      </div>
    </div>;
}
export default UserPhotoVideos;
function _temp(file) {
  if (file.type.startsWith("image/")) {
    return file.size < MAX_IMAGE_SIZE;
  }
  return file.size < MAX_VIDEO_SIZE;
}
function _temp2(files) {
  return files.every(_temp);
}